import {z as r} from 'zod';

import {UserConfigType} from '../user/user-config';
import {UserConfigKeyEnum} from '../user/user-config-const';

type ReviewsHiddenStatusesConfigType = Readonly<{
    hiddenStatusCodes: Array<string>;
}>;

const defaultHiddenStatusesConfig: ReviewsHiddenStatusesConfigType = {
    hiddenStatusCodes: [],
};

const storedHiddenStatusCodesSchema = r.array(r.string());

function isStoredReviewsHiddenStatusesConfig(
    value: UserConfigType[keyof UserConfigType] | undefined
): value is Partial<ReviewsHiddenStatusesConfigType> {
    return Boolean(value) && typeof value === 'object';
}

function getHiddenStatusCodes(value: unknown): Array<string> {
    const parsed = storedHiddenStatusCodesSchema.safeParse(
        (value as Partial<ReviewsHiddenStatusesConfigType> | undefined)?.hiddenStatusCodes
    );

    if (!parsed.success) {
        return [];
    }

    return [...new Set(parsed.data.map((code) => code.trim()).filter(Boolean))];
}

export function getReviewsHiddenStatusesConfig(userConfig?: UserConfigType): ReviewsHiddenStatusesConfigType {
    const value = userConfig?.[UserConfigKeyEnum.reviewsHiddenStatuses];

    if (!isStoredReviewsHiddenStatusesConfig(value)) {
        return defaultHiddenStatusesConfig;
    }

    return {
        hiddenStatusCodes: getHiddenStatusCodes(value),
    };
}

export function getUserConfigWithHiddenStatuses(
    userConfig: UserConfigType | undefined,
    hiddenStatusesConfig: ReviewsHiddenStatusesConfigType
): UserConfigType {
    return {
        ...userConfig,
        [UserConfigKeyEnum.reviewsHiddenStatuses]: hiddenStatusesConfig,
    };
}

export function addHiddenStatusCode(
    hiddenStatusCodes: Array<string>,
    statusCode: string
): ReviewsHiddenStatusesConfigType {
    const trimmedCode = statusCode.trim();

    if (!trimmedCode || hiddenStatusCodes.includes(trimmedCode)) {
        return {hiddenStatusCodes};
    }

    return {
        hiddenStatusCodes: [...hiddenStatusCodes, trimmedCode],
    };
}
